import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import CreateCrewDialog from "@/components/CreateCrewDialog";
import { getPlaylistCoverUrl, getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";

const KIND_BADGE = {
  crew: { label: "Crew", color: "bg-purple-600/30 text-purple-200 border-purple-500/40" },
  group: { label: "Group", color: "bg-pink-600/30 text-pink-200 border-pink-500/40" },
  club: { label: "Club", color: "bg-amber-600/30 text-amber-200 border-amber-500/40" },
};

const RANK_STYLE = [
  "bg-yellow-500/20 text-yellow-200 border-yellow-500/40",    // 1st
  "bg-gray-400/20 text-gray-100 border-gray-400/40",           // 2nd
  "bg-amber-700/20 text-amber-200 border-amber-700/40",        // 3rd
];

const SeederLeaderboard = ({ currentUser }) => {
  const [rows, setRows] = useState(null); // null=loading, []=empty
  const [error, setError] = useState(null);
  const [myRank, setMyRank] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/crew-seeder-leaderboard', {
          params: { period: 'month', limit: 10 },
        });
        if (!cancelled) setRows(r.data?.leaderboard || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Could not load leaderboard');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch caller's rank (auth required — skip for logged-out)
  useEffect(() => {
    if (!currentUser) { setMyRank(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/crew-seeder-leaderboard/my-rank', {
          params: { period: 'month' },
        });
        if (!cancelled) setMyRank(r.data);
      } catch {
        // fail silently — the banner is a bonus, not critical
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  if (error) return null; // fail-silent, don't break the page

  const myRankBanner = myRank ? (
    <div
      className={`mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border ${
        myRank.in_top_10
          ? 'bg-emerald-500/10 border-emerald-500/40'
          : 'bg-purple-500/10 border-purple-500/30'
      }`}
      data-testid="seeder-my-rank-banner"
    >
      <div className="flex items-center gap-2 text-sm">
        <span className="text-base">{myRank.in_top_10 ? '🌱' : '🎯'}</span>
        {myRank.in_top_10 ? (
          <span className="text-emerald-200 font-medium" data-testid="seeder-my-rank-text">
            You're in the Top 10 Crew Seeders this month 🌱
          </span>
        ) : myRank.rank ? (
          <span className="text-purple-100" data-testid="seeder-my-rank-text">
            Your rank:{' '}
            <span className="font-bold text-white">#{myRank.rank}</span>
            <span className="text-gray-400 mx-1.5">·</span>
            <span className="font-semibold text-white">{myRank.my_count}</span>{' '}
            join{myRank.my_count === 1 ? '' : 's'} this month
            <span className="text-gray-400 mx-1.5">·</span>
            <span className="text-emerald-200 font-semibold">
              {myRank.needed_for_top_10} more
            </span>{' '}
            to reach Top 10
          </span>
        ) : (
          <span className="text-purple-100" data-testid="seeder-my-rank-text">
            You haven't seeded any joins this month yet — share a crew to start climbing.
          </span>
        )}
      </div>
    </div>
  ) : null;

  if (rows === null) {
    return (
      <section className="mb-10" data-testid="seeder-leaderboard-loading">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span>🌱</span> Top Crew Seeders
            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 ml-1">This Month</span>
          </h2>
        </div>
        {myRankBanner}
        <Card className="p-4 bg-gray-900/40 border-gray-800">
          <div className="animate-pulse h-16 bg-gray-800/40 rounded"></div>
        </Card>
      </section>
    );
  }
  if (rows.length === 0) {
    return (
      <section className="mb-10" data-testid="seeder-leaderboard-empty">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span>🌱</span> Top Crew Seeders
            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 ml-1">This Month</span>
          </h2>
        </div>
        {myRankBanner}
        <Card className="p-5 bg-gray-900/40 border-gray-800 text-center">
          <p className="text-gray-400 text-sm">
            Be the first — share a crew this month and earn your <span className="text-emerald-300">🌱 Crew Seeder</span> badge.
          </p>
        </Card>
      </section>
    );
  }

  return (
    <section className="mb-10" data-testid="seeder-leaderboard">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <span>🌱</span> Top Crew Seeders
          <span
            className="text-xs font-normal px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 ml-1"
            data-testid="seeder-leaderboard-period"
          >This Month</span>
        </h2>
        <p className="text-xs text-gray-500">Who's growing RGM crews the most</p>
      </div>

      {/* Personal rank banner (auth only) */}
      {myRankBanner}

      <Card className="bg-gray-900/40 border-gray-800 overflow-hidden">
        <ul className="divide-y divide-gray-800">
          {rows.map((u, i) => {
            const hasBadge = (u.badges || []).includes('crew_seeder');
            const rankStyle = RANK_STYLE[i] || "bg-purple-500/10 text-purple-200 border-purple-500/20";
            return (
              <li key={u.user_id} data-testid={`seeder-row-${u.user_id}`}>
                <Link
                  to={`/artist/${u.user_id}`}
                  className="flex items-center gap-3 px-3 py-2 sm:px-4 sm:py-3 hover:bg-purple-500/10 transition-colors"
                >
                  <span
                    className={`w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-full border text-xs sm:text-sm font-bold flex items-center justify-center ${rankStyle}`}
                    aria-label={`rank ${i + 1}`}
                  >
                    {i + 1}
                  </span>
                  <img
                    src={getProfilePhotoUrl(u)}
                    alt={u.artist_name}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate flex items-center gap-1.5">
                      <span className="truncate">{u.artist_name}</span>
                      {hasBadge && (
                        <span
                          title="Crew Seeder"
                          className="text-xs"
                          data-testid={`seeder-badge-${u.user_id}`}
                        >🌱</span>
                      )}
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-500">
                      {u.crew_seeds_count} {u.crew_seeds_count === 1 ? 'join' : 'joins'} generated
                    </p>
                  </div>
                  <span className="text-emerald-300 text-sm font-bold shrink-0 tabular-nums" data-testid={`seeder-count-${u.user_id}`}>
                    +{u.crew_seeds_count}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </Card>
    </section>
  );
};

const CrewCard = ({ crew, onJoin, joining }) => {
  const badge = KIND_BADGE[crew.kind] || KIND_BADGE.crew;
  const coverUrl = getPlaylistCoverUrl({
    cover_image: crew.cover_image,
    cover_updated_at: crew.cover_updated_at,
  });
  return (
    <div className="group" data-testid={`crew-card-${crew.crew_id}`}>
      <Card className="overflow-hidden bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/60 transition-all h-full flex flex-col">
        <Link to={`/crews/${crew.crew_id}`} className="block">
          <div className="aspect-[16/9] relative overflow-hidden bg-gradient-to-br from-purple-900/40 to-pink-900/40">
            <img
              src={optimizeImage(coverUrl, 'card')}
              alt={crew.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                // Phase 15.6P Batch B — fail-soft to the branded RGM placeholder
                // (NOT an Unsplash photo) so crews without uploaded covers stay
                // on-brand.
                if (!e.target.dataset.fellback) {
                  e.target.dataset.fellback = "1";
                  e.target.src = "/rgm-logo.png";
                }
              }}
            />
            <div className="absolute top-2 left-2 flex gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full border ${badge.color}`}>
                {badge.label}
              </span>
              {crew.visibility === 'private' && (
                <span className="text-xs px-2 py-0.5 rounded-full border border-gray-500/40 bg-gray-700/60 text-gray-200">🔒 Private</span>
              )}
            </div>
          </div>
        </Link>
        <div className="p-4 flex-1 flex flex-col">
          <Link to={`/crews/${crew.crew_id}`} className="block">
            <h3 className="text-white font-bold truncate hover:text-purple-300">{crew.name}</h3>
            {crew.genre && <p className="text-purple-300 text-xs mt-0.5">{crew.genre}</p>}
            <p className="text-gray-400 text-sm mt-2 line-clamp-2 flex-1">
              {crew.description || <span className="italic text-gray-600">No description yet.</span>}
            </p>
          </Link>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800 text-xs">
            <span className="text-gray-400">👥 {crew.member_count} {crew.member_count === 1 ? 'member' : 'members'}</span>
            {crew.is_admin ? (
              <span className="text-purple-300">✨ Admin</span>
            ) : crew.is_member ? (
              <span className="text-green-300">Member</span>
            ) : onJoin && crew.visibility === 'public' ? (
              <Button
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  onJoin(crew.crew_id);
                }}
                disabled={joining}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white h-7 px-3 text-xs"
                data-testid={`card-join-${crew.crew_id}`}
              >
                {joining ? 'Joining…' : '+ Join'}
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  );
};

const ClubCard = ({ club }) => {
  const badge = KIND_BADGE.club;
  const coverUrl = getPlaylistCoverUrl({
    cover_image: club.cover_image,
    cover_updated_at: club.cover_updated_at,
  });
  const priceLabel = (club.price_monthly && club.price_monthly > 0)
    ? `$${Number(club.price_monthly).toFixed(2)}/mo`
    : 'Free';
  return (
    <div className="group" data-testid={`club-card-${club.club_id}`}>
      <Card className="overflow-hidden bg-gradient-to-br from-gray-900/80 to-black border-amber-500/20 hover:border-amber-500/60 transition-all h-full flex flex-col">
        <Link to={`/clubs/${club.club_id}`} className="block">
          <div className="aspect-[16/9] relative overflow-hidden bg-gradient-to-br from-amber-900/40 to-orange-900/40">
            <img
              src={optimizeImage(coverUrl, 'card')}
              alt={club.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                if (!e.target.dataset.fellback) {
                  e.target.dataset.fellback = "1";
                  e.target.src = "/rgm-logo.png";
                }
              }}
            />
            <div className="absolute top-2 left-2 flex gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full border ${badge.color}`}>
                {badge.label}
              </span>
              {club.visibility === 'private' && (
                <span className="text-xs px-2 py-0.5 rounded-full border border-gray-500/40 bg-gray-700/60 text-gray-200">🔒 Private</span>
              )}
            </div>
            <div className="absolute top-2 right-2">
              <span className="text-xs px-2 py-0.5 rounded-full border bg-black/60 border-amber-500/40 text-amber-200 font-semibold">
                {priceLabel}
              </span>
            </div>
          </div>
        </Link>
        <div className="p-4 flex-1 flex flex-col">
          <Link to={`/clubs/${club.club_id}`} className="block">
            <h3 className="text-white font-bold truncate hover:text-amber-300">{club.name}</h3>
            <p className="text-gray-400 text-sm mt-2 line-clamp-2 flex-1">
              {club.description || <span className="italic text-gray-600">No description yet.</span>}
            </p>
          </Link>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-800 text-xs">
            <span className="text-gray-400">⭐ {club.subscriber_count || 0} {club.subscriber_count === 1 ? 'subscriber' : 'subscribers'}</span>
            {club.is_owner ? (
              <span className="text-amber-300">✨ Owner</span>
            ) : club.is_subscriber ? (
              <span className="text-green-300">Subscribed</span>
            ) : (
              <Link
                to={`/clubs/${club.club_id}`}
                className="text-amber-300 hover:text-amber-200"
                data-testid={`card-view-club-${club.club_id}`}
              >
                View →
              </Link>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

const ClubsSection = ({ title, emoji, clubs, emptyText }) => {
  if (!clubs || clubs.length === 0) {
    if (!emptyText) return null;
    return (
      <section className="mb-8">
        <h2 className="text-white text-lg sm:text-xl font-bold mb-3 flex items-center gap-2">
          <span>{emoji}</span> {title}
        </h2>
        <p className="text-gray-500 text-sm italic">{emptyText}</p>
      </section>
    );
  }
  return (
    <section className="mb-8" data-testid={`clubs-section-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <h2 className="text-white text-lg sm:text-xl font-bold mb-3 flex items-center gap-2">
        <span>{emoji}</span> {title} <span className="text-gray-500 text-sm font-normal">({clubs.length})</span>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {clubs.map((c) => (
          <ClubCard key={c.club_id} club={c} />
        ))}
      </div>
    </section>
  );
};

const Section = ({ title, emoji, crews, onJoin, joiningId, emptyText }) => {
  if (!crews || crews.length === 0) {
    if (!emptyText) return null;
    return (
      <section className="mb-8">
        <h2 className="text-white text-lg sm:text-xl font-bold mb-3 flex items-center gap-2">
          <span>{emoji}</span> {title}
        </h2>
        <p className="text-gray-500 text-sm italic">{emptyText}</p>
      </section>
    );
  }
  return (
    <section className="mb-8" data-testid={`crews-section-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <h2 className="text-white text-lg sm:text-xl font-bold mb-3 flex items-center gap-2">
        <span>{emoji}</span> {title} <span className="text-gray-500 text-sm font-normal">({crews.length})</span>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {crews.map((c) => (
          <CrewCard
            key={c.crew_id}
            crew={c}
            onJoin={onJoin}
            joining={joiningId === c.crew_id}
          />
        ))}
      </div>
    </section>
  );
};

const CrewsPage = ({ defaultTab }) => {
  // Phase 19.0.1 — `defaultTab` is "club" when this page renders at /clubs,
  // "group" at /groups, and undefined at /crews. We use it to filter the
  // sections so each URL feels like its own destination (matches what
  // BackButton fallbacks promise).
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [data, setData] = useState({
    popular: [], new: [], mine: [],
    clubs: { popular: [], new: [], mine: [] },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joiningId, setJoiningId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Filter helpers
  const filterKind = (rows, kind) => (rows || []).filter((c) => (c.kind || "crew") === kind);
  const isClubMode = defaultTab === "club";
  const isGroupMode = defaultTab === "group";
  const visibleCrewMine    = isClubMode ? [] : isGroupMode ? filterKind(data.mine, "group")    : data.mine;
  const visibleCrewPopular = isClubMode ? [] : isGroupMode ? filterKind(data.popular, "group") : data.popular;
  const visibleCrewNew     = isClubMode ? [] : isGroupMode ? filterKind(data.new, "group")     : data.new;
  const visibleClubMine    = isGroupMode ? [] : data.clubs.mine;
  const visibleClubPopular = isGroupMode ? [] : data.clubs.popular;
  const visibleClubNew     = isGroupMode ? [] : data.clubs.new;

  const pageTitle = isClubMode
    ? { emoji: "🎟️", text: "Artist Clubs", sub: "Paid fan communities — exclusive drops, behind-the-scenes access, direct line to the artist." }
    : isGroupMode
      ? { emoji: "👥", text: "Groups", sub: "Public groups for collabs, scenes, and shared playlists." }
      : { emoji: "🤝", text: "Crews, Groups & Clubs", sub: "Find your people. Build a movement. Drop tracks, react, talk shop — or start a paid fan club." };

  useEffect(() => {
    api.get('/auth/me').then(r => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const fetchDiscover = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/crews/discover');
      setData({
        popular: r.data.popular || [],
        new: r.data.new || [],
        mine: r.data.mine || [],
        clubs: {
          popular: r.data.clubs?.popular || [],
          new: r.data.clubs?.new || [],
          mine: r.data.clubs?.mine || [],
        },
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load crews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDiscover(); }, []);

  const handleCreated = (record) => {
    setShowCreate(false);
    toast.success(`"${record.name}" created!`);
    if (record.kind === 'club' || record.club_id) {
      navigate(`/clubs/${record.club_id}`);
    } else {
      navigate(`/crews/${record.crew_id}`);
    }
  };

  const handleJoin = async (crewId) => {
    if (!user) {
      toast.info('Sign in to join crews');
      navigate('/login');
      return;
    }
    setJoiningId(crewId);
    try {
      await api.post(`/crews/${crewId}/join`);
      toast.success('Joined!');
      await fetchDiscover();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join');
    } finally {
      setJoiningId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="crews-page">
      <SharedNavigation currentUser={user} activePage="crews" />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
              <span>{pageTitle.emoji}</span> {pageTitle.text}
            </h1>
            <p className="text-gray-400 mt-1 text-sm">
              {pageTitle.sub}
            </p>
          </div>
          {user && (
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold"
              data-testid="create-crew-button"
            >
              + Create Crew / Club
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="crews-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-20" data-testid="crews-error">
            <p className="text-red-400 mb-3">⚠️ {error}</p>
            <Button onClick={fetchDiscover} variant="outline" className="text-white border-gray-700">
              Try again
            </Button>
          </div>
        ) : (
          <>
            {/* My Crews first (when logged in) */}
            {user && !isClubMode && (
              <Section
                title={isGroupMode ? "My Groups" : "My Crews"}
                emoji="🎵"
                crews={visibleCrewMine}
                onJoin={handleJoin}
                joiningId={joiningId}
                emptyText={isGroupMode
                  ? "You haven't joined any groups yet — see Popular and New below."
                  : "You haven't joined any crews yet — check Popular and New below."}
              />
            )}

            {/* My Clubs (owned or subscribed) */}
            {user && !isGroupMode && visibleClubMine.length > 0 && (
              <ClubsSection
                title="My Clubs"
                emoji="🎟️"
                clubs={visibleClubMine}
              />
            )}

            {/* Top Crew Seeders leaderboard */}
            {!isClubMode && <SeederLeaderboard currentUser={user} />}

            {!isClubMode && (
              <>
                <Section
                  title={isGroupMode ? "Popular Groups" : "Popular"}
                  emoji="🔥"
                  crews={visibleCrewPopular}
                  onJoin={handleJoin}
                  joiningId={joiningId}
                />

                <Section
                  title={isGroupMode ? "New Groups" : "New"}
                  emoji="✨"
                  crews={visibleCrewNew}
                  onJoin={handleJoin}
                  joiningId={joiningId}
                />
              </>
            )}

            {/* Dedicated Clubs discovery */}
            {!isGroupMode && (visibleClubPopular.length > 0 || visibleClubNew.length > 0) && (
              <div
                className={isClubMode ? "" : "mt-10 pt-8 border-t border-amber-500/20"}
                data-testid="clubs-discovery-block"
              >
                {!isClubMode && (
                  <div className="mb-5">
                    <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
                      <span>🎟️</span> Artist Clubs
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">
                      Paid fan communities — get exclusive drops, behind-the-scenes, and direct access.
                    </p>
                  </div>
                )}

                <ClubsSection
                  title="Popular Clubs"
                  emoji="🔥"
                  clubs={visibleClubPopular}
                />

                <ClubsSection
                  title="New Clubs"
                  emoji="✨"
                  clubs={visibleClubNew}
                />
              </div>
            )}

            {/* Completely empty state */}
            {visibleCrewPopular.length === 0 && visibleCrewNew.length === 0
              && visibleClubPopular.length === 0 && visibleClubNew.length === 0
              && (!user || (visibleCrewMine.length === 0 && visibleClubMine.length === 0)) && (
              <div className="text-center py-12" data-testid="crews-empty">
                <p className="text-5xl mb-3">🌱</p>
                <p className="text-white text-lg font-semibold">
                  {isClubMode ? "No public clubs yet — be the first."
                    : isGroupMode ? "No public groups yet — be the first."
                    : "No public crews or clubs yet — be the first."}
                </p>
                <p className="text-gray-400 mt-2 text-sm">
                  Crews are mini-communities for collabs and feedback. Clubs are monetization-ready fan communities.
                </p>
                {user && (
                  <Button
                    onClick={() => setShowCreate(true)}
                    className="mt-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                    data-testid="empty-create-crew-button"
                  >
                    + Start a Crew or Club
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <CreateCrewDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default CrewsPage;
